import {Amplify} from 'aws-amplify'
import {fetchAuthSession} from 'aws-amplify/auth'
import {Hub} from 'aws-amplify/utils'
import {setAuthenticated} from '../store/authSlice'
import {setUser} from '../store/userSlice'
import {Dispatch} from '@reduxjs/toolkit'
import {getConfig} from './config'
import {NavigateFunction} from 'react-router-dom'
import {getLogger} from './logging'
import {User} from '../types/app-types'
import {JwtPayload} from 'jsonwebtoken'

const logger = getLogger('auth')

// configure amplify authentication w/ cognito user pool
const {cognitoUserPoolId, cognitoWebClientId} = getConfig()

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: cognitoUserPoolId,
      userPoolClientId: cognitoWebClientId
    }
  }
})

function attributesToUser(attributes: JwtPayload): User {
  return {
    email: attributes.email,
    firstName: attributes.given_name,
    lastName: attributes.family_name,
    subject: attributes.sub,
    groups: attributes['cognito:groups'] || []
  }
}

async function handleSignIn(dispatch: Dispatch): Promise<void> {
  const session = await fetchAuthSession()
  const attributes = session.tokens.idToken.payload
  logger.logInfo('User attributes', attributes)
  dispatch(setAuthenticated(true))
  dispatch(setUser(attributesToUser(attributes)))
}

function handleSignOut(dispatch: Dispatch, navigate: NavigateFunction): void {
  dispatch(setAuthenticated(false))
  navigate('/')
}

function createAuthListener(dispatch: Dispatch, navigate: NavigateFunction) {
  return async (data: any): Promise<void> => {
    switch (data?.payload?.event) {
      case 'signedIn':
        logger.logInfo('User signed in')
        await handleSignIn(dispatch)
        break
      case 'signedOut':
        logger.logInfo('User signed out')
        handleSignOut(dispatch, navigate)
        break
      case 'tokenRefresh':
        logger.logInfo('Auth token refresh succeeded')
        break
      case 'tokenRefresh_failure':
        logger.logError('Auth token refresh failed')
        break
      default:
        logger.logWarn('Unhandled auth event:', data?.payload?.event)
    }
  }
}

async function initAuthState(dispatch: Dispatch) {
  logger.logInfo('Initializing auth state...')
  try {
    const session = await fetchAuthSession()
    logger.logInfo('User session', session)

    if (session?.tokens?.idToken) {
      logger.logInfo('User is already signed in')
      const attributes = session.tokens.idToken.payload
      logger.logInfo('User attributes', attributes)
      dispatch(setAuthenticated(true))
      dispatch(setUser(attributesToUser(attributes)))
    } else {
      logger.logInfo('User is not signed in')
      dispatch(setAuthenticated(false))
    }
  } catch (e) {
    logger.logInfo('User is not signed in')
    dispatch(setAuthenticated(false))
  }
}

export function initAuthListener(
  dispatch: Dispatch,
  navigate: NavigateFunction
) {
  return () => {
    // init auth state of current user and setup auth event listeners
    initAuthState(dispatch).then(() => {
      const authListener = createAuthListener(dispatch, navigate)
      const unsubscribe = Hub.listen('auth', authListener)
    })
  }
}
